// Voltaje de alimentación y de la batería interna del equipo, para el informe
// «Voltaje y energía» (en Mapon: «Suministro de voltaje» + «Sin energía»).
//
// Las escalas vienen mezcladas (medido el 23-oct-2026 en 300 equipos-día de
// history_positions): power_supply_voltage llega en mV (p50 13129 = 13.1 V;
// p95 27900 = camiones de 24 V) y external_power_voltage a veces en V (12.438)
// y a veces en mV (14243). Ningún vehículo pasa de 40 V, así que >100 es mV.
// battery_voltage (batería interna, Li-ion ~4.06 V) trae además valores de
// ~93 000: el porcentaje ×1000 de v4-edge (bug conocido); fuera de 2.5-5 V se
// descarta. Los eventos 18/19 (desconexión/conexión) no vienen en las
// posiciones, así que el corte se detecta por voltaje.

export const MAX_VEHICLE_VOLTS = 40;

// Con la alimentación desconectada el equipo vive de su batería interna y
// reporta 0-4 V; un vehículo encendido o en reposo nunca baja de 6 V.
export const CUT_VOLTS = 6;

export const toVolts = (raw: unknown): number | null => {
  const n = typeof raw === "string" ? parseFloat(raw) : typeof raw === "number" ? raw : NaN;
  if (!Number.isFinite(n)) return null;
  const v = n > 100 ? n / 1000 : n;
  return v >= 0 && v <= MAX_VEHICLE_VOLTS ? v : null;
};

type Params = Record<string, unknown> | null | undefined;

export const supplyVolts = (params: Params): number | null =>
  toVolts(params?.power_supply_voltage) ?? toVolts(params?.external_power_voltage);

export const backupVolts = (params: Params): number | null => {
  for (const key of ["battery_voltage", "pwr_int", "battery_raw"]) {
    const v = toVolts(params?.[key]);
    if (v != null && v >= 2.5 && v <= 5) return v;
  }
  return null;
};

/** 12 o 24 V según la mediana de las lecturas con alimentación. */
export const nominalSystem = (volts: number[]): 12 | 24 => {
  const on = volts.filter((v) => v >= CUT_VOLTS).sort((a, b) => a - b);
  if (on.length === 0) return 12;
  return on[Math.floor(on.length / 2)] > 18 ? 24 : 12;
};

// Batería de 12 V en reposo sana: 12.4-12.7 V; con el alternador, 13.5-14.5.
// Debajo de 11.5 V ya no arranca de forma confiable. En 24 V, el doble.
export const lowVolts = (system: 12 | 24): number => (system === 24 ? 23 : 11.5);

export interface VoltPoint {
  ts: number;
  volts: number;
}

export interface VoltEpisode {
  kind: "cut" | "low";
  start: number;
  end: number;
  minVolts: number;
  points: number;
}

/**
 * Tramos seguidos de puntos que cumplen `test`. Termina en el primer punto que
 * ya no cumple (ahí volvió la energía) o en el último del tramo. Un punto
 * suelto no cuenta: una lectura aislada en 0 V es ruido del sensor.
 */
export const episodes = (
  points: VoltPoint[],
  kind: VoltEpisode["kind"],
  test: (v: number) => boolean,
): VoltEpisode[] => {
  const out: VoltEpisode[] = [];
  let cur: VoltEpisode | null = null;
  for (const p of points) {
    if (test(p.volts)) {
      if (!cur) cur = { kind, start: p.ts, end: p.ts, minVolts: p.volts, points: 0 };
      cur.end = p.ts;
      cur.minVolts = Math.min(cur.minVolts, p.volts);
      cur.points++;
    } else if (cur) {
      cur.end = p.ts;
      if (cur.points > 1) out.push(cur);
      cur = null;
    }
  }
  if (cur && cur.points > 1) out.push(cur);
  return out;
};

export const powerEpisodes = (points: VoltPoint[], system: 12 | 24): VoltEpisode[] => {
  const low = lowVolts(system);
  return [
    ...episodes(points, "cut", (v) => v < CUT_VOLTS),
    ...episodes(points, "low", (v) => v >= CUT_VOLTS && v < low),
  ].sort((a, b) => a.start - b.start);
};
